import React, { useMemo, useState } from 'react'
import { OptionSelect } from '@/core/utils/types'
import { Hint, Input, Select } from '@tecsinapse/cortex-react'
import { t } from '@lingui/core/macro'
import { ErrorMessage } from '@hookform/error-message'
import { useController, useFormContext } from 'react-hook-form'

interface FormMultiGroupedSelectProps {
  name: string
  label?: string
  options: Map<any, any>
  customOnChange?: (e: unknown[]) => void
  disabled?: boolean
  id?: string
  withSearch?: boolean
  groupedLabelExtractor: (label: string) => string
}

export const FormMultiGroupedSelect = ({
  name,
  label,
  options: _options = [],
  customOnChange,
  disabled,
  id,
  withSearch = false,
  groupedLabelExtractor
}: FormMultiGroupedSelectProps) => {
  const { setValue, control } = useFormContext()
  const {
    field: { value = [] },
    formState: { errors, submitCount },
    fieldState: { error }
  } = useController({ name, control })
  const [searchText, setSearchText] = useState('')

  const labelExtractor = (option: OptionSelect) => option.label
  const keyExtractor = (option: OptionSelect) => option.value

  const filteredOptions = useMemo(() => {
    const newMap = new Map()
    Array.from(_options.entries()).forEach(entry => {
      const [key, items] = entry as [number, OptionSelect[]]
      const itemsFiltered = items.filter(item => {
        return new RegExp(searchText, 'ig').test(item.label)
      })
      if (itemsFiltered.length) newMap.set(key, itemsFiltered)
    })
    return newMap
  }, [_options, searchText])

  const options = useMemo(
    () => (withSearch ? filteredOptions : _options),
    [filteredOptions, _options, withSearch]
  )

  return (
    <div className={'w-full'}>
      <Select.Root
        value={value}
        keyExtractor={keyExtractor}
        labelExtractor={labelExtractor}
      >
        <Select.Trigger
          label={label}
          disabled={disabled}
          type={'button'}
          multiLabelSelected={numSelecteds =>
            t`${numSelecteds} itens selecionados`
          }
          id={id}
        />
        <Select.Popover>
          {withSearch ? (
            <div className='pt-deca px-deca'>
              <Input.Search
                onChange={e => setSearchText?.(e.target.value)}
                defaultValue={searchText}
              />
            </div>
          ) : null}
          <Select.MultiGroupedOptions
            onSelect={e => {
              setValue(name, e, {
                shouldValidate: true,
                shouldDirty: true,
                shouldTouch: true
              })
              if (customOnChange) {
                customOnChange(e)
              }
            }}
            options={options}
            groupedLabelExtractor={groupedLabelExtractor}
          >
            <Select.MultiCheckAllOptions checkAllLabel={t`Selecionar todos`} />
          </Select.MultiGroupedOptions>
        </Select.Popover>
      </Select.Root>
      {submitCount > 0 && error ? (
        <ErrorMessage
          errors={errors}
          name={name}
          render={({ message }) => (
            <Hint variants={{ intent: 'error' }}>{message}</Hint>
          )}
        />
      ) : null}
    </div>
  )
}
